import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Input,
  // Select,
  Switch,
  Upload,
  // Divider,
  Row,
  Col,
  Space,
  message,
  Checkbox,
} from "antd";
import {
  CloseOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import CSVFormatModal from "./CSVFormatModal";
import DPCard from "./DPCard";
import PincodeApi from "../../api/PincodeApi";
import "./AddPincodeModal.scss";

const { Dragger } = Upload;

const AddPincodeModal = ({
  visible,
  onCancel,
  mode = "add",
  pincodeData,
  activeTab = "ecomm",
  onSuccess,
}) => {
  const [form, setForm] = useState({
    pincode: "",
    city: "",
    state: "",
    status: true,
    dps: [],
  });
  const [isCSVModalVisible, setIsCSVModalVisible] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedPincodeOption, setSelectedPincodeOption] = useState(null);
  // const [pincodeOptions] = useState([]);
  const [availableDPs] = useState([]);

  useEffect(() => {
    if (mode === "edit" && pincodeData) {
      setForm({
        pincode: pincodeData.pincode,
        city: pincodeData.city,
        state: pincodeData.state,
        status: pincodeData.status,
        dps: pincodeData.dps || [],
      });
      setSelectedPincodeOption({
        value: pincodeData.pincode,
        label: `${pincodeData.pincode} - ${pincodeData.city} - ${pincodeData.state}`,
        city: pincodeData.city,
        state: pincodeData.state,
      });
    } else {
      resetForm();
    }
  }, [mode, pincodeData, visible]);

  const resetForm = () => {
    setForm({
      pincode: "",
      city: "",
      state: "",
      status: true,
      dps: [],
    });
    setSelectedPincodeOption(null);
    setUploadedFile(null);
  };

  // const handlePincodeSelect = (value, option) => {
  //   setSelectedPincodeOption(option);
  //   setForm(prev => ({
  //     ...prev,
  //     pincode: value,
  //     city: option.city,
  //     state: option.state
  //   }));
  // };

  const handleAddDP = (selectedDPs) => {
    const newDPs = selectedDPs.map((dpId) => {
      const dp = availableDPs.find((d) => d.id === dpId);
      return {
        id: dpId,
        name: dp?.name || "Lorem Ipsum Delivery Partner Name",
        dpId: "1453729",
        type: "Hub_DP",
        status: true,
        fmHub: "",
        lmHub: "",
        services: {
          ECOM: {
            FM: true,
            LM: true,
            COD: true,
            RTO: true,
            SDD: true,
            NDD: true,
            AD: true,
          },
          Courier: {
            FM: true,
            LM: true,
            COD: true,
            RTO: true,
            SDD: true,
            NDD: true,
            AD: true,
          },
          Cargo: {
            FM: true,
            LM: true,
            COD: true,
            RTO: true,
            SDD: true,
            NDD: true,
            AD: true,
          },
        },
      };
    });

    setForm((prev) => ({
      ...prev,
      dps: [...prev.dps, ...newDPs],
    }));
  };

  const handleRemoveDP = (dpId) => {
    setForm((prev) => ({
      ...prev,
      dps: prev.dps.filter((dp) => dp.id !== dpId),
    }));
  };

  const handleDPUpdate = (dpId, updatedDP) => {
    setForm((prev) => ({
      ...prev,
      dps: prev.dps.map((dp) =>
        dp.id === dpId ? { ...dp, ...updatedDP } : dp
      ),
    }));
  };

  const handleUpload = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx,.xls",
    maxCount: 1,
    beforeUpload: (file, fileList) => {
      // Ensure only one file can be selected
      if (fileList.length > 1) {
        message.error("You can only select one file at a time!");
        return false;
      }

      // If there's already a file, replace it
      if (uploadedFile) {
        message.info("Previous file replaced with new selection");
      }

      const isValidFile =
        file.type === "text/csv" ||
        file.name.endsWith(".csv") ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls");

      if (!isValidFile) {
        message.error("You can only upload CSV or Excel files!");
        return false;
      }

      setUploadedFile(file);
      return false; // Prevent auto upload
    },
    onRemove: () => {
      setUploadedFile(null);
    },
    fileList: uploadedFile ? [uploadedFile] : [],
  };

  const handleBulkUpload = async (retryCount = 0) => {
    if (!uploadedFile) {
      message.error("Please select a file to upload");
      return;
    }

    // Show loading state
    const loadingMessage = message.loading(
      retryCount > 0
        ? `Retrying upload... (Attempt ${retryCount + 1})`
        : "Uploading file...",
      0
    );

    try {
      const response = await PincodeApi.bulkUploadPincodeServiceability(
        uploadedFile
      );

      // Hide loading message
      loadingMessage();

      if (response) {
        const { success, inserted_count = 0, failed_rows = [] } = response;

        if (success && inserted_count > 0) {
          // Successful upload with some records inserted
          message.success(
            `Successfully uploaded! ${inserted_count} records inserted.`
          );

          if (failed_rows.length > 0) {
            message.warning(`${failed_rows.length} rows failed to upload.`);
          }

          setUploadedFile(null);
          onSuccess();
        } else if (!success && inserted_count === 0 && failed_rows.length > 0) {
          // Upload failed - no records inserted, but we have error details
          message.error(
            `Upload failed! All ${failed_rows.length} rows failed to process.`
          );

          // Show detailed error information
          const errorSummary = {};
          failed_rows.forEach((row) => {
            const error = row.error;
            errorSummary[error] = (errorSummary[error] || 0) + 1;
          });

          // Show user-friendly error message
          const errorTypes = Object.keys(errorSummary);
          if (errorTypes.length === 1) {
            const mainError = errorTypes[0];

            // Use API error message directly
            message.error(mainError);
          } else {
            // Multiple error types - show simple message
            // message.error(`Upload failed with ${errorTypes.length} different errors. Please check your file.`);
          }
        } else if (success && inserted_count === 0) {
          // Edge case: success but no records inserted
          message.warning(
            "Upload completed but no records were inserted. Please check your file format."
          );
        } else {
          // Unexpected response format
          message.error(
            "Upload completed but response format is unexpected. Please check the console."
          );
        }
      } else {
        message.error("No response received from server. Please try again.");
      }
    } catch (error) {
      // Hide loading message
      loadingMessage();

      // More detailed error handling
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        if (status === 400) {
          message.error(
            `Bad Request: ${data.message || "Invalid file format or content"}`
          );
        } else if (status === 413) {
          message.error("File too large. Please try a smaller file.");
        } else if (status === 415) {
          message.error(
            "Unsupported file type. Please use CSV or Excel files only."
          );
        } else if (status === 502) {
          // Retry 502 errors up to 2 times
          if (retryCount < 2) {
            setTimeout(() => {
              handleBulkUpload(retryCount + 1);
            }, 2000);
            return;
          } else {
            message.error(
              "Server temporarily unavailable (502 Bad Gateway). Please try again later or contact support."
            );
          }
        } else if (status === 503) {
          message.error(
            "Service temporarily unavailable (503). Please try again later."
          );
        } else if (status === 504) {
          message.error(
            "Request timeout (504). The file might be too large or server is busy. Please try again."
          );
        } else if (status >= 500) {
          message.error(
            `Server error (${status}). Please try again later or contact support.`
          );
        } else {
          message.error(
            `Upload failed: ${data.message || "Unknown server error"}`
          );
        }
      } else if (error.request) {
        // Network error
        message.error(
          "Network error. Please check your connection and try again."
        );
      } else {
        // Other error
        console.error("Upload error:", error.message);
        message.error(`Upload failed: ${error.message}`);
      }
    }
  };

  // const handleSubmit = async () => {
  //   // If there's an uploaded file, this should not be called - use handleBulkUpload instead
  //   if (uploadedFile) {
  //     message.error(
  //       "Please use the bulk upload functionality for file uploads"
  //     );
  //     return;
  //   }

  //   // Only validate pincode for manual form submission
  //   if (!form.pincode) {
  //     message.error("Please select a pincode");
  //     return;
  //   }

  //   try {
  //     const apiData = {
  //       pincode: form.pincode,
  //       state: form.state,
  //       city: form.city,
  //       is_active: form.isActive,
  //       dps: form.dps.map((dp) => ({
  //         id: dp.id,
  //         name: dp.name,
  //         service_types: dp.services,
  //         is_active: dp.isActive,
  //       })),
  //     };

  //     if (mode === "add") {
  //       await PincodeApi.createPincodeServiceability(apiData);
  //       message.success(`Pincode ${form.pincode} added successfully!`);
  //     } else {
  //       await PincodeApi.updatePincodeServiceability(form.pincode, apiData);
  //       message.success(`Pincode ${form.pincode} updated successfully!`);
  //     }

  //     onSuccess();
  //   } catch (error) {
  //     console.error("Error submitting form:", error);
  //     message.error(
  //       `Failed to ${
  //         mode === "add" ? "add" : "update"
  //       } pincode. Please try again.`
  //     );
  //   }
  // };

  const renderDPAssignmentModal = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [selectedDPs, setSelectedDPs] = useState([]);

    const handleAssignDP = () => {
      setIsVisible(true);
    };

    const handleConfirmAssign = () => {
      if (selectedDPs.length > 0) {
        handleAddDP(selectedDPs);
        setSelectedDPs([]);
        setIsVisible(false);
      }
    };

    return (
      <>
        <Button
          type="primary"
          ghost
          icon={<PlusOutlined />}
          onClick={handleAssignDP}
          className="assign-dp-btn"
        >
          ASSIGN DP
        </Button>

        <Modal
          title="Assign DP"
          visible={isVisible}
          onCancel={() => setIsVisible(false)}
          onOk={handleConfirmAssign}
          okText="Assign"
          cancelText="Cancel"
          width={400}
        >
          <div className="assign-dp-modal">
            <Input
              placeholder="Search DP"
              prefix={<SearchOutlined />}
              className="search-dp-input"
            />
            <div className="dp-list">
              {availableDPs.map((dp) => (
                <div key={dp.id} className="dp-option">
                  <Checkbox
                    checked={selectedDPs.includes(dp.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDPs([...selectedDPs, dp.id]);
                      } else {
                        setSelectedDPs(
                          selectedDPs.filter((id) => id !== dp.id)
                        );
                      }
                    }}
                  >
                    {dp.name}
                  </Checkbox>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      </>
    );
  };

  return (
    <>
      <Modal
        title={
          <div className="modal-header">
            <span className="modal-title">
              {mode === "add" 
                ? `Add Pincode - ${activeTab === "courier" ? "Courier" : "Ecomm"}`
                : `Edit Pincode - ${activeTab === "courier" ? "Courier" : "Ecomm"}`}
            </span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onCancel}
              className="modal-close-btn"
            />
          </div>
        }
        visible={visible}
        footer={null}
        width={720}
        className="add-pincode-modal"
        closeIcon={null}
        closable={false}
      >
        <div className="modal-content">
          {/* Bulk Upload Section */}
          <div className="bulk-upload-section">
            <div className="bulk-upload-header">
              <span>Bulk Upload</span>
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                onClick={() => {
                  const templateUrls = {
                    ecomm: "https://delcaper-prod.s3.ap-south-1.amazonaws.com/ath-template/Ecomm-bulk-upload-pincode.xlsx",
                    courier: "https://delcaper-prod.s3.ap-south-1.amazonaws.com/ath-template/Courier-bulk-upload-pincode.xlsx",
                  };

                  const url = templateUrls[activeTab] || templateUrls.ecomm;
                  const fileName = activeTab === "courier" 
                    ? "Courier-bulk-upload-pincode.xlsx"
                    : "Ecomm-bulk-upload-pincode.xlsx";

                  const link = document.createElement("a");
                  link.href = url;
                  link.download = fileName;
                  link.target = "_blank";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="csv-info-btn"
              >
                Download Excel Template
              </Button>
            </div>
            <div className="upload-section">
              {!uploadedFile ? (
                <Dragger {...handleUpload} className="csv-uploader compact">
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: "var(--primary-blue)" }} />
                  </p>
                  <p className="ant-upload-text">
                    Drag and drop your CSV file here, or click to browse
                  </p>
                  <p className="ant-upload-hint">Supports: .csv, .xlsx, .xls</p>
                </Dragger>
              ) : (
                <div className="selected-file-container">
                  <div className="selected-file-info">
                    <div className="file-icon">
                      <InboxOutlined />
                    </div>
                    <div className="file-details">
                      <div className="file-name">{uploadedFile.name}</div>
                      <div className="file-size">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={() => setUploadedFile(null)}
                      className="remove-file-btn"
                      title="Remove file"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* <Divider className="section-divider">OR</Divider> */}

          {/* Select Pincode Section */}
          {/* <div className="select-pincode-section">
            <div className="section-header">
              <h3>Select Pincode</h3>
            </div>
            <Select
              showSearch
              placeholder="Search Pincode"
              value={selectedPincodeOption?.value}
              onChange={handlePincodeSelect}
              options={pincodeOptions}
              className="pincode-select"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div> */}

          {/* Pincode Details Section */}
          {selectedPincodeOption && (
            <div className="pincode-details-section">
              <Row gutter={24} className="pincode-info-grid">
                <Col span={6}>
                  <div className="info-item">
                    <label>Pincode</label>
                    <div className="value-with-edit">
                      <span>{form.pincode}</span>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                      />
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="info-item">
                    <label>City</label>
                    <span>{form.city}</span>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="info-item">
                    <label>State</label>
                    <span>{form.state}</span>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="info-item">
                    <label>Status</label>
                    <Switch
                      checked={form.status}
                      onChange={(checked) =>
                        setForm((prev) => ({ ...prev, status: checked }))
                      }
                      className="status-switch"
                    />
                  </div>
                </Col>
              </Row>

              {/* DPs Section */}
              <div className="dps-section">
                <div className="section-header">
                  <h3>Delivery Partners</h3>
                  {renderDPAssignmentModal()}
                </div>

                {form.dps.length === 0 ? (
                  <div className="no-dp-assigned">
                    <span>No Delivery Partner Assigned</span>
                  </div>
                ) : (
                  <div className="dp-cards">
                    {form.dps.map((dp) => (
                      <DPCard
                        key={dp.id}
                        dp={dp}
                        onUpdate={(updatedDP) =>
                          handleDPUpdate(dp.id, updatedDP)
                        }
                        onRemove={() => handleRemoveDP(dp.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <Space size={12}>
            <Button onClick={onCancel} className="close-btn-custom">
              Close
            </Button>
            <Button
              type={uploadedFile && uploadedFile.name ? "primary" : "default"}
              onClick={() => handleBulkUpload()}
              className="add-btn-custom"
            >
              {mode === "add" ? "Add" : "Update"}
            </Button>
          </Space>
        </div>
      </Modal>

      <CSVFormatModal
        visible={isCSVModalVisible}
        onCancel={() => setIsCSVModalVisible(false)}
      />
    </>
  );
};

export default AddPincodeModal;
